import { SparkIcon } from '../SparkIcon';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import './TagGeneral.css';

export type TagGeneralVariant = 'default' | 'closeable' | 'add-new';

/** General tag chip. Supports default, closeable (with ×), and add-new (+) variants, plus a checkable selected state. */
export type TagGeneralProps = {
  label?: string;
  variant?: TagGeneralVariant;
  /** Selected state — blue surface, inverse text (Figma Checkable=Yes). */
  checked?: boolean;
  onClose?: () => void;
  /** Storybook-only — force close icon visible for static matrices. */
  showClose?: boolean;
  /**
   * Optional 16px icon after the label (Figma `9150:4071` plan chip: `Pro` + bolt).
   * Decorative only; it inherits the tag colour and is hidden from assistive tech.
   */
  endIconSlug?: string;
  className?: string;
};

export function TagGeneral({
  label = 'Tag',
  variant = 'default',
  checked = false,
  onClose,
  showClose = false,
  endIconSlug,
  className = '',
}: TagGeneralProps) {
  const rootClasses = [
    'spark-tag-general',
    `spark-tag-general-${variant}`,
    checked ? 'spark-tag-general-checked' : '',
    showClose ? 'spark-tag-general-show-close' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const displayLabel = variant === 'add-new' ? 'New Tag' : label;

  return (
    <span className={rootClasses}>
      {variant === 'add-new' ? <SparkIcon slug="plus" size="small" className="spark-tag-general-icon" /> : null}
      <span className="spark-tag-general-label">{displayLabel}</span>
      {endIconSlug ? (
        <SparkIcon slug={endIconSlug} size="small" className="spark-tag-general-icon spark-tag-general-end-icon" />
      ) : null}
      {variant === 'closeable' ? (
        onClose ? (
          <TooltipTrigger text={`Remove ${label}`}>
            <button
              type="button"
              className="spark-tag-general-close"
              aria-label={`Remove ${label}`}
              // Removing a tag must not read as a click/focus on the field that
              // hosts it (multi-selects open their dropdown on those events).
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                onClose();
              }}
            >
              <SparkIcon slug="close" size="small" className="spark-tag-general-close-icon" />
            </button>
          </TooltipTrigger>
        ) : (
          <span className="spark-tag-general-close" aria-hidden="true">
            <SparkIcon slug="close" size="small" className="spark-tag-general-close-icon" />
          </span>
        )
      ) : null}
    </span>
  );
}
